import { useEffect, useState, useCallback, useRef } from "react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { Bus, Plus, Trash2, Loader2, Pencil, MapPin, Users, QrCode, Search, Clock } from "lucide-react";
import { IconAction } from "@/components/IconAction";
import { useAuth } from "@/context/AuthContext";
import BusStatusDialog, { BusStatusChip } from "@/components/BusStatusDialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function PanToBus({ bus }) {
  const map = useMap();
  useEffect(() => {
    if (map && bus && bus.last_lat != null && bus.last_lng != null) {
      map.panTo({ lat: bus.last_lat, lng: bus.last_lng });
      map.setZoom(15);
    }
  }, [map, bus]);
  return null;
}

function AnimatedBusMarker({ bus, live, selected, onClick }) {
  const [pos, setPos] = useState({ lat: bus.last_lat, lng: bus.last_lng });
  const posRef = useRef({ lat: bus.last_lat, lng: bus.last_lng });

  useEffect(() => {
    const from = { ...posRef.current };
    const to = { lat: bus.last_lat, lng: bus.last_lng };
    if (from.lat === to.lat && from.lng === to.lng) return;
    const start = performance.now();
    const dur = 1800;
    let raf;
    const step = (t) => {
      const k = Math.min((t - start) / dur, 1);
      const e = 1 - Math.pow(1 - k, 3);
      const next = { lat: from.lat + (to.lat - from.lat) * e, lng: from.lng + (to.lng - from.lng) * e };
      posRef.current = next;
      setPos(next);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [bus.last_lat, bus.last_lng]);

  return (
    <AdvancedMarker position={pos} title={`Bus ${bus.number}${live ? " · live" : ""}`} onClick={onClick}>
      <div className={`relative w-20 transition ${selected ? "scale-110 drop-shadow-[0_0_10px_rgba(29,78,216,0.85)]" : "drop-shadow-lg"} ${live ? "" : "grayscale opacity-70"}`}
           data-testid={`bus-marker-${bus.id}`}>
        <img src="/bus-marker.png" alt={`Bus ${bus.number}`} className="w-full select-none pointer-events-none" draggable={false} />
        <span className="absolute left-[42%] top-[68%] -translate-x-1/2 -translate-y-1/2 rounded-md bg-slate-900/90 px-1.5 py-0.5 text-[11px] font-black leading-none text-white">
          {bus.number}
        </span>
        {live && <span className="absolute -top-1.5 right-0 h-3.5 w-3.5 rounded-full bg-emerald-500 border-2 border-white animate-pulse" />}
      </div>
    </AdvancedMarker>
  );
}

export default function BusesPage() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "corporate_admin";
  const [statusFor, setStatusFor] = useState(null);
  const [buses, setBuses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | bus
  const [form, setForm] = useState({ number: "", driver_id: "", school_ids: [] });
  const [siblings, setSiblings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedBusId, setSelectedBusId] = useState(null);
  const [q, setQ] = useState("");
  const mapRef = useRef(null);

  const focusBus = (b) => {
    if (b.last_lat == null || b.last_lng == null) {
      toast(`Bus ${b.number} hasn't reported GPS yet — ask the driver to tap "Share GPS".`);
      return;
    }
    setSelectedBusId(b.id);
    mapRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const load = useCallback(async () => {
    try {
      const [b, o] = await Promise.all([api.get("/buses"), canManage ? api.get("/officers") : Promise.resolve({ data: [] })]);
      setBuses(b.data);
      setDrivers(o.data.filter((u) => u.role === "driver"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    if (!canManage) return;
    api.get("/buses/sharable-schools").then(({ data }) => setSiblings(data)).catch(() => {});
  }, [canManage]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing?.id) await api.put(`/buses/${editing.id}`, form);
      else await api.post("/buses", form);
      toast.success(editing?.id ? "Bus updated" : "Bus added");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete bus ${b.number}? Its students will be unassigned.`)) return;
    try {
      await api.delete(`/buses/${b.id}`);
      toast.success(`Bus ${b.number} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const bql = q.trim().toLowerCase();
  const visibleBuses = buses.filter((b) =>
    !bql || String(b.number).toLowerCase().includes(bql) || (b.driver_name || "").toLowerCase().includes(bql)
  );
  const located = buses.filter((b) => b.last_lat != null && b.last_lng != null);
  const center = located[0] ? { lat: located[0].last_lat, lng: located[0].last_lng } : { lat: 32.7767, lng: -96.797 };
  const isLive = (b) => b.last_loc_at && Date.now() - new Date(b.last_loc_at).getTime() < 60000;
  const selectedBus = buses.find((b) => b.id === selectedBusId) || null;

  return (
    <div className="p-6 md:p-10" data-testid="buses-page">
      <header className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Buses</h1>
          <p className="text-sm text-slate-500">Fleet, drivers, live locations and today's running status.</p>
        </div>
        {canManage && <div className="flex gap-2">
          <button
            onClick={async () => {
              try {
                const token = localStorage.getItem("vlog_token");
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/students/bus-badges.pdf`, {
                  headers: token ? { Authorization: `Bearer ${token}` } : {},
                });
                if (!res.ok) {
                  const d = await res.json().catch(() => ({}));
                  throw new Error(d.detail || "No bus riders to print badges for");
                }
                const blob = await res.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = "logerd-bus-badges.pdf";
                document.body.appendChild(a);
                a.click();
                a.remove();
                toast.success("Bus badges downloaded — print, cut and hand them out");
              } catch (e) {
                toast.error(e.message || "Failed");
              }
            }}
            data-testid="print-bus-badges-btn"
            title="Printable QR badges students scan when boarding or leaving the bus"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
          >
            <QrCode className="h-4 w-4" /> Bus badges
          </button>
          <button
            onClick={() => { setEditing({}); setForm({ number: "", driver_id: "", school_ids: [] }); }}
            data-testid="add-bus-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:shadow transition"
          >
            <Plus className="h-4 w-4" /> Add bus
          </button>
        </div>}
      </header>

      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 text-sm font-semibold text-slate-900">
          <MapPin className="h-4 w-4 text-primary" /> Live bus map
          <span className="text-xs font-normal text-slate-400">{located.length} of {buses.length} buses reporting GPS</span>
          {buses.some((b) => isLive(b)) && (
            <span className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-[10px] font-bold text-emerald-700" data-testid="map-live-count">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              {buses.filter((b) => isLive(b)).length} LIVE NOW
            </span>
          )}
        </div>
        <div className="h-[380px]" data-testid="bus-map">
          {!MAPS_KEY ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-6" data-testid="bus-map-nokey">
              <MapPin className="h-8 w-8 text-slate-300 mb-3" />
              <div className="font-medium text-slate-700">Google Maps key not configured</div>
              <p className="text-xs text-slate-500 mt-1 max-w-sm">
                Add your key as <code className="bg-slate-100 px-1 rounded">REACT_APP_GOOGLE_MAPS_API_KEY</code> to see buses live on the map. Everything else works without it.
              </p>
            </div>
          ) : (
            <APIProvider apiKey={MAPS_KEY}>
              <Map
                style={{ width: "100%", height: "100%" }}
                defaultCenter={center}
                defaultZoom={12}
                mapId="logerd-buses"
                gestureHandling="greedy"
                disableDefaultUI={true}
                zoomControl={true}
              >
                <PanToBus bus={selectedBus} />
                {located.map((b) => (
                  <AnimatedBusMarker key={b.id} bus={b} live={isLive(b)}
                                     selected={selectedBusId === b.id}
                                     onClick={() => setSelectedBusId(b.id)} />
                ))}
              </Map>
            </APIProvider>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="buses-search-input"
              placeholder="Search bus number or driver…"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
            />
          </div>
          {bql && <div className="text-xs text-slate-500">{visibleBuses.length} of {buses.length} buses</div>}
        </div>
        <table className="w-full text-sm" data-testid="buses-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-5 py-3">Bus</th>
              <th className="px-5 py-3">Driver</th>
              <th className="px-5 py-3">Riders</th>
              <th className="px-5 py-3">On bus now</th>
              <th className="px-5 py-3">Today's status</th>
              <th className="px-5 py-3">Last GPS</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">Loading…</td></tr>}
            {!loading && buses.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">No buses yet — add your first bus.</td></tr>}
            {!loading && buses.length > 0 && visibleBuses.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">No buses match your search.</td></tr>}
            {visibleBuses.map((b) => (
              <tr key={b.id} className="border-t border-slate-100" data-testid={`bus-row-${b.id}`}>
                <td className="px-5 py-3">
                  <button
                    onClick={() => focusBus(b)}
                    data-testid={`bus-locate-${b.id}`}
                    title={b.last_lat != null ? "Show on map" : "No GPS reported yet"}
                    className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-bold transition ${
                      selectedBusId === b.id
                        ? "bg-primary text-white shadow"
                        : "bg-amber-100 text-amber-800 hover:bg-amber-200 hover:shadow-sm"
                    }`}
                  >
                    <Bus className="h-3.5 w-3.5" /> {b.number}
                    <MapPin className="h-3 w-3 opacity-60" />
                  </button>
                </td>
                <td className="px-5 py-3 text-slate-700">
                  {b.driver_name || <span className="text-slate-400">Unassigned</span>}
                  {b.shared_with?.length > 0 && (
                    <div className="mt-0.5 text-[10px] text-blue-600" data-testid={`bus-sharedwith-${b.id}`}>
                      Also serves: {b.shared_with.join(", ")}
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 text-slate-700">{b.rider_count}</td>
                <td className="px-5 py-3">
                  <span
                    data-testid={`bus-onboard-${b.id}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${
                      b.onboard_count > 0 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    <Users className="h-3 w-3" /> {b.onboard_count || 0} on board
                  </span>
                </td>
                <td className="px-5 py-3">
                  <BusStatusChip bus={b} testid={`bus-status-chip-${b.id}`} />
                  {(b.status === "delayed" || b.sub_number) && b.status_by && (
                    <div className="mt-0.5 text-[10px] text-slate-400" data-testid={`bus-status-by-${b.id}`}>
                      {b.status_note ? `“${b.status_note}” · ` : ""}by {b.status_by}
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 text-xs">
                  {b.last_loc_at ? (
                    <span className="inline-flex items-center gap-1.5 text-slate-500">
                      {isLive(b) && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700" data-testid={`bus-live-${b.id}`}>
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
                        </span>
                      )}
                      {new Date(b.last_loc_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                    </span>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex items-center gap-0.5">
                    <IconAction onClick={() => setStatusFor(b)} Icon={Clock} label="Set status / substitute bus" tone={b.status === "delayed" ? "danger" : "slate"} testid={`bus-status-btn-${b.id}`} />
                    {b.is_owner === false ? (
                      <span className="ml-1 inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-semibold text-blue-700" data-testid={`bus-shared-tag-${b.id}`}>
                        Shared with us
                      </span>
                    ) : canManage && (
                      <>
                        <IconAction
                          onClick={() => { setEditing(b); setForm({ number: b.number, driver_id: b.driver_id || "", school_ids: b.school_ids || [] }); }}
                          Icon={Pencil} label="Edit" tone="primary" testid={`edit-bus-${b.id}`}
                        />
                        <IconAction onClick={() => remove(b)} Icon={Trash2} label="Delete" tone="danger" testid={`delete-bus-${b.id}`} />
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {statusFor && <BusStatusDialog bus={statusFor} onClose={() => setStatusFor(null)} onSaved={load} />}

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="bus-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Bus className="h-5 w-5 text-primary" /> {editing?.id ? `Edit bus ${editing.number}` : "Add bus"}
            </DialogTitle>
            <DialogDescription>Give the bus a number and assign its driver. Drivers are employees with the "Bus driver" role.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 pt-1">
            <label className="block">
              <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">Bus number</span>
              <input required value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} data-testid="bus-number-input"
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" placeholder="12" />
            </label>
            <label className="block">
              <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">Driver</span>
              <select value={form.driver_id} onChange={(e) => setForm({ ...form, driver_id: e.target.value })} data-testid="bus-driver-select"
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary outline-none">
                <option value="">Unassigned</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              {drivers.length === 0 && <span className="block mt-1 text-[10px] text-slate-400">No drivers yet — add an employee with the "Bus driver" role first.</span>}
            </label>
            {siblings.length > 0 && (
              <div>
                <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">Also serves these schools</span>
                <div className="space-y-1.5 max-h-36 overflow-y-auto rounded-lg border border-slate-200 p-2.5" data-testid="bus-schools-list">
                  {siblings.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                      <input type="checkbox" checked={form.school_ids.includes(s.id)}
                        data-testid={`bus-school-${s.id}`}
                        onChange={(e) => setForm((f) => ({
                          ...f,
                          school_ids: e.target.checked ? [...f.school_ids, s.id] : f.school_ids.filter((x) => x !== s.id),
                        }))}
                        className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/30" />
                      {s.name}
                    </label>
                  ))}
                </div>
                <span className="block mt-1 text-[10px] text-slate-400">
                  Shared buses appear on each school's map, and the driver sees riders from every school on the route.
                </span>
              </div>
            )}
            <DialogFooter>
              <button type="submit" disabled={saving} data-testid="bus-save-btn"
                className="h-10 rounded-lg bg-primary px-5 text-sm font-semibold text-white inline-flex items-center justify-center gap-2 hover:shadow disabled:opacity-60 transition">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Save bus
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
